'use client';

import { Box, Button, Container, Paper, Typography } from '@mui/material';

// Import Lora font from Google Fonts
import '@fontsource/lora/700.css';
import Grid from '@mui/material/Grid2';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Calendar, MapPin, SearchIcon, TargetIcon, Ticket } from 'lucide-react';
import { useBoolean } from 'minimal-shared/hooks';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { ButtonJumbo } from 'src/components/button-jumbo/button-jumbo';
import { DialogAgentPicker } from 'src/components/dialogs/dialog-agent-picker/dialog-agent-picker';
import { DialogDatePicker, useDatePicker } from 'src/components/dialogs/dialog-date-picker';
import { DialogSeatCountField, useSeatCount } from 'src/components/dialogs/dialog-seat-count-field';
import { Iconify } from 'src/components/iconify';
import { Image } from 'src/components/image';
import { TextFieldJumboView } from 'src/components/textfield-jumbo/textfield-jumbo-view';
import type { Agent } from 'src/data/models/agent.model';
import { paths } from 'src/routes/paths';
import { themeConfig } from 'src/theme';
import { queryParamToString } from 'src/utils/general_helper';

const todayDate = (): Dayjs => {
  const date = dayjs();
  const formatted = date.format('YYYY-MM-DD');
  return dayjs(formatted);
};

const tomorrowDate = () => todayDate().add(1, 'day');

export default function Header() {
  const router = useRouter();
  const seatPicker = useSeatCount();
  const datePicker = useDatePicker(dayjs(), null);
  const openOriginAgentDialog = useBoolean();
  const openDestinationAgentDialog = useBoolean();

  const [agent, setAgent] = useState<Agent | null>(null);
  const [destinationAgent, setDestinationAgent] = useState<Agent | null>(null);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [seatCount, setSeatCount] = useState<number>(1);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const searchTicket = () => {
    if (!agent) {
      setErrorMessage('Silakan pilih agen asal.');
      return;
    }
    if (!destinationAgent) {
      setErrorMessage('Silakan pilih agen tujuan.');
      return;
    }
    if (!date) {
      setErrorMessage('Silakan pilih tanggal pergi.');
      return;
    }
    if (!seatCount || seatCount <= 0) {
      setErrorMessage('Silakan pilih jumlah kursi.');
      return;
    }

    // Clear error message if all validations pass
    setErrorMessage(null);

    const params = {
      agent: agent?._id,
      toAgent: destinationAgent?._id,
      date: date?.format('YYYY-MM-DD'),
      seat: seatCount,
    };
    const queryParams = queryParamToString(params);
    const url = `${paths.cariTiket}?${queryParams}`;
    router.push(url);
  };

  return (
    <Box
      sx={{
        paddingBottom: '16px',
        borderRadius: '0px 0px 50px 50px',
        borderBottom: '5px solid var(--secondary-main, #FEFE01)',
        background:
          'var(--gradient-primary-bold, linear-gradient(135deg, var(--primary-main, #11BB18) 0%, var(--primary-dark, #007867) 100%))',
        boxShadow:
          'var(--color-x, 0px) var(--color-y, 8px) var(--color-blur, 16px) var(--color-spread, 0px) var(--primary-24, rgba(0, 167, 111, 0.24))',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          borderRadius: '0px 0px 50px 50px',
          backgroundImage: {
            xs: 'url(/assets/images/background-home-mobile.webp)',
            sm: 'url(/assets/images/background-home.webp)',
          },
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      >
        <Box
          sx={{
            padding: { xs: '0px 0 24px 0', md: '0px 16px 91px 16px' },
            borderRadius: '0px 0px 50px 50px',
          }}
        >
          <Container>
            <Grid
              container
              direction="column"
              sx={{
                justifyContent: { xs: 'flex-start', md: 'space-between' },
                alignItems: { xs: 'center', md: 'flex-end' },
              }}
            >
              <Grid size={12}>
                <div className="flex flex-col items-center gap-4 lg:block lg:relative pt-[40px] lg:pt-[40px]">
                  <Grid
                    container
                    direction={{
                      xs: 'column-reverse',
                      md: 'row',
                    }}
                    spacing={2}
                    sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', textAlign: 'center' }}>
                      <Image
                        sx={{
                          width: {
                            xs: '200px',
                            md: '100%',
                          },
                          m: '0px auto',
                        }}
                        src="/assets/images/bus-with-people.webp"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        variant="h1"
                        textAlign={{ xs: 'center', md: 'right' }}
                        sx={{
                          color: 'var(--primary-contrast-text, #FFF)',
                          overflow: 'hidden',
                          fontSize: {
                            xs: '20px !important',
                            md: '45px !important',
                          },
                          fontFamily: 'Lora, serif',
                          fontWeight: 700,
                        }}
                      >
                        Selalu Siap Menjadi Bagian Dalam Cerita Perjalanan Anda.
                      </Typography>
                    </Grid>
                  </Grid>
                </div>
              </Grid>
              <Grid size={12}>
                <div className="flex justify-end">
                  <Box
                    sx={{
                      position: 'relative',
                      paddingTop: '50px',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '0px',
                        left: { xs: '0px', md: 'unset' },
                        right: '32px',
                        zIndex: '9',
                        display: 'flex',
                      }}
                    >
                      <Box
                        sx={{
                          background: themeConfig.gradient.primary.bold,
                          border: '1px solid var(--primary-light, #0CE615)',
                          padding: '10px 20px 26px 20px',
                          borderRadius: '20px 20px 0 0',
                          display: 'flex',
                          alignItems: 'center',
                          margin: '0 auto',
                          gap: 1,
                        }}
                      >
                        <Ticket color="white" />
                        <Typography variant="h5" component="span" color="white">
                          CARI TIKET BUS
                        </Typography>
                      </Box>
                    </Box>
                    <Paper
                      sx={{
                        width: '100%',
                        borderRadius: '20px',
                        padding: { xs: '20px', md: '28px' },
                        zIndex: '10',
                        position: 'relative',
                      }}
                    >
                      <Grid
                        container
                        spacing={2}
                        sx={{ alignItems: 'start', justifyContent: 'start' }}
                      >
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextFieldJumboView
                            icon={<MapPin color="white" />}
                            label="Asal Keberangkatan"
                            value={agent?.name}
                            onClick={openOriginAgentDialog.onTrue}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextFieldJumboView
                            icon={<TargetIcon color="white" />}
                            label="Tujuan Keberangkatan"
                            value={destinationAgent?.name}
                            onClick={openDestinationAgentDialog.onTrue}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 8, md: 8 }}>
                              <TextFieldJumboView
                                icon={<Calendar color="white" />}
                                label="Tanggal Pergi"
                                placeholder="2025/01/01"
                                value={date ? date.format('YYYY/MM/DD') : ''}
                                onClick={datePicker.onOpen}
                              />
                            </Grid>
                            <Grid size={{ xs: 4, md: 4 }}>
                              <Grid container spacing="4px">
                                <Grid size={12}>
                                  <Button
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setDate(todayDate())}
                                    color={
                                      date.format('YYYY-MM-DD') == todayDate().format('YYYY-MM-DD')
                                        ? 'primary'
                                        : 'inherit'
                                    }
                                  >
                                    Hari Ini
                                  </Button>
                                </Grid>
                                <Grid size={12}>
                                  <Button
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setDate(tomorrowDate())}
                                    color={
                                      date.format('YYYY-MM-DD') ==
                                      tomorrowDate().format('YYYY-MM-DD')
                                        ? 'primary'
                                        : 'inherit'
                                    }
                                  >
                                    Besok
                                  </Button>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <TextFieldJumboView
                            icon={<Iconify icon="icon-park-outline:baby-car-seat" color="white" />}
                            label="Jumlah Kursi"
                            placeholder="1 Kursi"
                            value={seatCount ? `${seatCount} kursi` : null}
                            onClick={seatPicker.onOpen}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <ButtonJumbo onClick={searchTicket} startIcon={<SearchIcon />}>
                            Cari Tiket
                          </ButtonJumbo>
                          {errorMessage && (
                            <Typography
                              variant="body2"
                              color="error"
                              sx={{ mt: 1, textAlign: 'center' }}
                            >
                              {errorMessage}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>
                </div>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
      <DialogAgentPicker
        title="Pilih Agen Asal"
        open={openOriginAgentDialog.value}
        onClose={openOriginAgentDialog.onFalse}
        onSubmit={(e) => setAgent(e as Agent)}
      />

      <DialogAgentPicker
        title="Pilih Tujuan Keberangkatan"
        open={openDestinationAgentDialog.value}
        onClose={openDestinationAgentDialog.onFalse}
        onSubmit={(e) => setDestinationAgent(e as Agent)}
      />

      <DialogSeatCountField
        title="Atur Jumlah Kursi"
        open={seatPicker.open}
        seatCount={seatPicker.seatCount}
        onChangeSeatCount={seatPicker.onChangeSeatCount}
        onClose={seatPicker.onClose}
        onSubmit={(e) => setSeatCount(e as number)}
      />

      <DialogDatePicker
        variant="calendar"
        open={datePicker.open}
        title="Tanggal Pergi"
        minDate={dayjs()}
        selectedDate={datePicker.selectedDate}
        onChangeSelectedDate={datePicker.onChangeSelectedDate}
        onClose={datePicker.onClose}
        onSubmit={(d) => setDate(d as Dayjs)}
      />
    </Box>
  );
}
